// ./src/store/slices/bitmapSlice.ts
import React from "react";
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {getToken} from "../../utils/api";

// Define the state interface for bitmap
export interface BitmapState {
    cardDesignBitmapUrl?: string;
    cardDesignBitmapFilename?: string;
    lastUpdatedAt?: number; // Timestamp for cache busting
    cardBitmapIsDirty: boolean; // True when card has unsaved visual changes
    loading: boolean;  // To track the loading state for async actions
    error: string | null;  // To store any error messages
}

// Set the initial state
const initialState: BitmapState = {
    cardDesignBitmapUrl: undefined,
    cardDesignBitmapFilename: undefined,
    lastUpdatedAt: undefined,
    cardBitmapIsDirty: false,
    loading: false,
    error: null,
};

// Async thunk to upload card screenshot bitmap image

export const uploadBitmapImage = createAsyncThunk('bitmap/uploadBitmapImage',
    async (formData: FormData, { rejectWithValue }) => {
        const token = getToken();
        if (!token) return rejectWithValue('No token found');

        try {
            const response = await fetch(
                `${process.env.GATSBY_BACKEND_URL}/wp-json/invidoo/v1/upload-card-bitmap-image`,
                {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                }
            );
            if (!response.ok) throw new Error('Failed to upload card bitmap image');
            const result = await response.json();
            return { url: result.url };
        } catch (error) {
            if (error instanceof Error) {
                // Access error properties safely
                return rejectWithValue(error.message);
            } else {
                // Handle unexpected error types
                return rejectWithValue('Unknown error occurred during bitmap upload');
            }
        }
    }
);

// Async thunk to update project with the bitmap URL
export const updateProjectWithBitmapUrl = createAsyncThunk(
    'bitmap/updateProjectWithBitmapUrl',
    async ({ projectId, bitmapUrl }: { projectId: number; bitmapUrl: string; }, { rejectWithValue }) => {
        const token = getToken();
        if (!token) return rejectWithValue('No token found');

        try {
            const response = await fetch(
                `${process.env.GATSBY_BACKEND_URL}/wp-json/invidoo/v1/update-card-bitmap-url/${projectId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ carddesign_bitmap: bitmapUrl }),
                }
            );
            if (!response.ok) throw new Error('Failed to update project with bitmap URL');
            return await response.json();
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Unknown error occurred during project update');
        }
    }
);


// Create the slice
const bitmapSlice = createSlice({
    name: 'bitmap',
    initialState,
    reducers: {
        setCardDesignBitmapUrl(state, action: PayloadAction<string>) {
            state.cardDesignBitmapUrl = action.payload;
            state.lastUpdatedAt = Date.now(); // Set timestamp for cache busting
        },
        setCardDesignBitmapFilename(state, action: PayloadAction<string>) {
            state.cardDesignBitmapFilename = action.payload;
            state.lastUpdatedAt = Date.now(); // Update timestamp when filename changes too
        },
        // NEW: Action to update timestamp when any design changes occur
        updateBitmapTimestamp(state) {
            state.lastUpdatedAt = Date.now();
            state.cardBitmapIsDirty = true; // Mark card as having unsaved visual changes
        },
        // Clear dirty flag (called after bitmap is regenerated/saved)
        clearCardBitmapDirty(state) {
            state.cardBitmapIsDirty = false;
        },
        resetBitmapState(state) {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle the uploadBitmapImage thunk
            .addCase(uploadBitmapImage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(uploadBitmapImage.fulfilled, (state, action: PayloadAction<{ url: string }>) => {
                state.cardDesignBitmapUrl = action.payload.url;  // Update the state with the URL
                state.lastUpdatedAt = Date.now(); // Set timestamp for cache busting
                state.cardBitmapIsDirty = false; // Card bitmap is now up-to-date
                state.loading = false;
            })
            .addCase(uploadBitmapImage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Handle the updateProjectWithBitmapUrl thunk
            .addCase(updateProjectWithBitmapUrl.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateProjectWithBitmapUrl.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(updateProjectWithBitmapUrl.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
    },
});

// Export the actions and reducer
export const {
    setCardDesignBitmapUrl,
    setCardDesignBitmapFilename,
    updateBitmapTimestamp,
    clearCardBitmapDirty,
    resetBitmapState
} = bitmapSlice.actions;

export default bitmapSlice.reducer;
